import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2, PencilLine, ShieldAlert } from 'lucide-react';
import { getErrorMessage } from '../../../lib/apiClient';
import { fetchFileBlob } from '../filesApi';
import { isMalwareBlocked, MALWARE_BLOCKED_MESSAGE } from '../malware';
import { canOpenInEditor, isEditableInEditor } from '../../editor/editorApi';
import InlinePreview from './InlinePreview';

function kindOf(mime = '') {
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('text/') || mime === 'application/json') return 'text';
  return 'other';
}

/**
 * Owner/member preview: fetches the file's bytes through the authenticated
 * workspace-scoped API, then hands them to InlinePreview for rendering. Fills
 * its parent — give it a sized container. Office documents (no native inline
 * renderer) offer to open in the ONLYOFFICE editor instead of dead-ending.
 */
export default function FilePreview({ file }) {
  const navigate = useNavigate();
  const mime = file?.mimeType ?? '';
  const kind = useMemo(() => kindOf(mime), [mime]);

  const [blob, setBlob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Set when the server says the file is still being scanned (outside uploads).
  const [scanPending, setScanPending] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const blocked = isMalwareBlocked(file);

  useEffect(() => {
    // A flagged file is never fetched: the server refuses it anyway.
    if (!file || blocked) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setScanPending(null);
    setBlob(null);
    fetchFileBlob(file.id)
      .then((b) => !cancelled && setBlob(b))
      .catch((error) => {
        if (cancelled) return;
        if (error?.response?.status === 409) setScanPending(getErrorMessage(error));
        else setFailed(true);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [file, blocked, attempt]);

  if (blocked) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <ShieldAlert className="h-10 w-10 text-red-600" aria-hidden="true" />
        <p className="text-sm font-medium text-slate-700">Blocked by the malware scan</p>
        <p className="max-w-sm text-sm text-slate-500">{MALWARE_BLOCKED_MESSAGE}</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (scanPending) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" aria-hidden="true" />
        <p className="max-w-sm text-sm text-slate-600">{scanPending}</p>
        <button
          type="button"
          onClick={() => setAttempt((n) => n + 1)}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        >
          Try again
        </button>
      </div>
    );
  }
  if (failed) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-slate-400">
        <FileText className="h-10 w-10" />
        <p className="text-sm">Couldn’t load a preview.</p>
      </div>
    );
  }

  // Office documents (and other editor-supported types) don't render inline —
  // offer to open them in the ONLYOFFICE editor instead of dead-ending.
  if (kind === 'other' && canOpenInEditor(file)) {
    const editable = isEditableInEditor(file);
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center text-slate-400">
        <FileText className="h-12 w-12" />
        <div>
          <p className="text-sm font-medium text-slate-600">
            {editable ? 'This document opens in the editor' : 'This document opens in the viewer'}
          </p>
          <p className="text-xs">{mime || 'document'}</p>
        </div>
        <button
          onClick={() => navigate(`/files/${file.id}/edit`)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-400"
        >
          <PencilLine className="h-4 w-4" />
          {editable ? 'Open in editor' : 'Open document'}
        </button>
      </div>
    );
  }

  return <InlinePreview mimeType={file?.mimeType} name={file?.name} blob={blob} />;
}
